import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/infrastructure", label: "Strategic Infrastructure" },
  { to: "/technology", label: "Advanced Intelligence" },
  { to: "/contact", label: "Contact Us" },
  { to: "/admin", label: "Admin Portal" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b transition-all duration-300 px-6 sm:px-12 ${
        scrolled ? "bg-[#0B0D12]/92 border-[#282E3F] h-16" : "bg-[#0B0D12]/85 border-[#282E3F]/80 h-20"
      }`}
    >
      <div className="max-w-7xl mx-auto h-full flex items-center justify-between">
        <Logo compact={scrolled} />
        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] font-medium tracking-wide transition-colors duration-200 ${
                  isActive ? "text-sky-400" : "text-slate-300 hover:text-slate-100"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="header-briefing-cta"
            className="group flex items-center gap-1.5 text-[13px] font-semibold tracking-wide text-white bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] px-4 py-2 rounded-full hover:shadow-[0_0_24px_rgba(59,130,246,0.4)] transition-shadow duration-300"
          >
            Request Institutional Briefing
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>
        <button
          className="lg:hidden text-slate-200 p-2"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden absolute top-full left-0 right-0 bg-[#0B0D12]/97 backdrop-blur-xl border-b border-[#282E3F] px-6 py-6 flex flex-col gap-4"
            data-testid="mobile-nav"
          >
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-base font-medium ${isActive ? "text-sky-400" : "text-slate-300"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
